/**
 * Pure conditional-visibility resolver for native surveys (VER-109).
 *
 * A {@link BranchingRule} shows or hides a question / section based on a prior
 * numeric answer. This module is the single source of truth for "what is visible
 * right now" — the runner uses it to decide what to render, and the scorer uses
 * it so hidden (skipped) questions never count toward the total or the sum.
 *
 * Visibility model (per target):
 *   - "show" rules: target is HIDDEN by default, revealed when ANY of its show
 *     rules is satisfied.
 *   - "hide" rules: target is VISIBLE by default, hidden when ANY of its hide
 *     rules is satisfied.
 *   - both: visible = (noShowRules || anyShowSatisfied) && !anyHideSatisfied.
 *   - no rules: always visible.
 * A rule whose source question is unanswered never fires.
 */
import type { BranchingRule, BranchOperator, SurveyDefinition, SurveyResponseValue } from "./survey.types";

/** Compare a single numeric answer against a rule's operator + value. */
const compareNumber = (answer: number, operator: BranchOperator, value: number): boolean => {
    switch (operator) {
        case "eq":
            return answer === value;
        case "neq":
            return answer !== value;
        case "gt":
            return answer > value;
        case "gte":
            return answer >= value;
        case "lt":
            return answer < value;
        case "lte":
            return answer <= value;
        default:
            return false;
    }
};

/**
 * Does a rule's source answer satisfy the rule?
 * - number answer → numeric comparison
 * - number[] (multi) → eq = "includes value", neq = "excludes value"; ordered
 *   comparisons are not meaningful for a set and never fire
 * - "" / string / undefined → not satisfied (unanswered or non-numeric source)
 */
export const ruleSatisfied = (rule: BranchingRule, answer: SurveyResponseValue | undefined): boolean => {
    if (answer === undefined || answer === "") {
        return false;
    }
    if (typeof answer === "number") {
        return compareNumber(answer, rule.operator, rule.value);
    }
    if (Array.isArray(answer)) {
        if (rule.operator === "eq") {
            return answer.includes(rule.value);
        }
        if (rule.operator === "neq") {
            return !answer.includes(rule.value);
        }
        return false;
    }
    // string (freeText) sources cannot drive numeric branching
    return false;
};

export type VisibilityResult = {
    hiddenSectionIds: Set<string>;
    /** All hidden question ids, INCLUDING every question inside a hidden section. */
    hiddenQuestionIds: Set<string>;
};

const isTargetVisible = (rules: BranchingRule[], values: Record<string, SurveyResponseValue>): boolean => {
    if (rules.length === 0) {
        return true;
    }
    const showRules = rules.filter((r) => r.action === "show");
    const hideRules = rules.filter((r) => r.action === "hide");

    const anyShowSatisfied = showRules.some((r) => ruleSatisfied(r, values[r.sourceQuestionId]));
    const anyHideSatisfied = hideRules.some((r) => ruleSatisfied(r, values[r.sourceQuestionId]));

    const shownByDefault = showRules.length === 0;
    return (shownByDefault || anyShowSatisfied) && !anyHideSatisfied;
};

/**
 * Resolve which sections + questions are hidden for a given answer set.
 * Questions inside a hidden section are always reported hidden.
 */
export const resolveVisibility = (
    survey: SurveyDefinition,
    values: Record<string, SurveyResponseValue>,
): VisibilityResult => {
    const hiddenSectionIds = new Set<string>();
    const hiddenQuestionIds = new Set<string>();

    const rulesForTarget = (targetType: "question" | "section", targetId: string): BranchingRule[] =>
        survey.branchingRules.filter((r) => r.targetType === targetType && r.targetId === targetId);

    for (const section of survey.sections) {
        const sectionVisible = isTargetVisible(rulesForTarget("section", section.id), values);
        if (!sectionVisible) {
            hiddenSectionIds.add(section.id);
        }
        for (const question of section.questions) {
            const questionVisible = sectionVisible && isTargetVisible(rulesForTarget("question", question.id), values);
            if (!questionVisible) {
                hiddenQuestionIds.add(question.id);
            }
        }
    }

    return { hiddenSectionIds, hiddenQuestionIds };
};
